import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import ReactMarkdown from "react-markdown";import remarkGfm from "remark-gfm";
import {
  Send, Loader2, Sparkles, User, MessageSquare, RefreshCw, DollarSign, Building, Rocket, HelpCircle, Bookmark, LogOut, BookmarkCheck, Copy, Check, History, Trash2, ChevronLeft, ChevronRight, BookMarked, Info, ChevronDown, ExternalLink, MapPin, Download, Star, X, Pencil, Columns
} from "lucide-react";
import { ContactModal } from "../components/ContactModal";
import { ExperienceFeedbackWidget } from "../components/ExperienceFeedbackWidget";
import { ProgramCompareModal } from "../components/ProgramCompareModal";
import { useVisitTracking } from "../hooks/useVisitTracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

const getSessionId = () => {
  let sessionId = localStorage.getItem("cml_session_id");
  if (!sessionId) {
    sessionId = "session_" + Math.random().toString(36).substr(2, 9) + "_" + Date.now();
    localStorage.setItem("cml_session_id", sessionId);
  }
  return sessionId;
};

// Autocomplete suggestions - common queries and sentence completions
const AUTOCOMPLETE_SUGGESTIONS = [
  // Introductions
  "I'm a robotics startup looking for",
  "I'm building a hardware company and need",
  "I'm an early-stage founder seeking",
  "I'm a manufacturing startup interested in",
  "I'm looking for funding for my",
  "I need help finding resources for",
  
  // Funding related
  "Show me funding options for",
  "What grants are available for",
  "Are there any free programs for",
  "What accelerators support",
  "Show me recommendations for funding",
  "What non-dilutive funding is available",
  "What loans are available for startups",
  
  // Specific requests
  "Tell me more about the ARM Institute",
  "Tell me more about xBridge",
  "Tell me more about Innovation Works",
  "What is the best option for",
  "How do I apply to",
  "What are the requirements for",
  
  // Stage-specific
  "I'm at the idea stage and",
  "I'm at MVP stage looking for",
  "I'm at growth stage seeking",
  "I have a prototype and need",
  
  // Industry-specific
  "robotics startups in Pittsburgh",
  "manufacturing support in the area",
  "hardware development resources",
  "automation companies",
  "warehouse robotics funding",
  
  // Actions
  "Can you recommend programs for",
  "What resources are available for",
  "Show me a list of",
  "Compare options for",
];

// Quick action buttons configuration
const QUICK_ACTIONS = [
  { label: "Show me recommendations", icon: Rocket, message: "Can you show me some recommendations based on what I've told you?" },
  { label: "Funding options", icon: DollarSign, message: "What funding options would work for my situation?" },
  { label: "Free resources", icon: Building, message: "What free resources or programs are available?" },
  { label: "Tell me more", icon: HelpCircle, message: "Can you tell me more about the top recommendation?" },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // User state
  const [user, setUser] = useState(location.state?.user || null);
  const [copiedId, setCopiedId] = useState(null);
  
  // Sidebar state
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('history'); // 'history' or 'saved'
  const [conversations, setConversations] = useState([]);
  const [savedRecs, setSavedRecs] = useState([]);
  const [loadingSidebar, setLoadingSidebar] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(getSessionId());
  const [savedProgramNames, setSavedProgramNames] = useState([]);
  const [selectedSaved, setSelectedSaved] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showConcierge, setShowConcierge] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(() => (typeof window !== 'undefined' && localStorage.getItem('cml_privacy_accepted') === 'true'));
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [ratingClosed, setRatingClosed] = useState(false);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [existingRating, setExistingRating] = useState(null);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingTags, setRatingTags] = useState([]);
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  
  const [suggestions, setSuggestions] = useState([
    "I'm an early-stage startup looking for funding",
    "What manufacturing resources are available?",
    "Show me free programs for startups",
    "What accelerators can help with product development?"
  ]);
  
  // Autocomplete state
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [selectedAutocompleteIndex, setSelectedAutocompleteIndex] = useState(0);
  
  const userMessageCount = messages.filter(m => m.role === "user").length;
  const visitId = useVisitTracking(userMessageCount, { privacyAccepted, typed: hasTyped });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Check for existing user session on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (location.state?.user) {
        setUser(location.state.user);
        return;
      }
      
      if (window.location.hash?.includes('session_id=')) {
        return;
      }
      
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
          if (response.ok) {
            const verifiedUser = await response.json();
            setUser(verifiedUser);
          } else {
            localStorage.removeItem('user');
          }
        } catch (error) {
          localStorage.removeItem('user');
        }
      }
    };
    checkAuth();
  }, [location.state]);

  useEffect(() => {
    axios.get(API + "/chat/history", { params: { session_id: currentSessionId } })
      .then(function(res) { 
        if (res.data.messages && res.data.messages.length > 0) {
          setMessages(res.data.messages); 
        }
      })
      .catch(function(err) { console.error(err); });
  }, [currentSessionId]);

  // Load existing rating (if any) for the current conversation and reset rating UI
  useEffect(() => {
    setRatingClosed(false);
    setRatingStars(0);
    setRatingComment('');
    setRatingTags([]);
    setExistingRating(null);
    fetch(`${API}/ratings?conversation_id=${currentSessionId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { rating: null })
      .then(d => {
        if (d.rating) {
          setExistingRating(d.rating);
          setRatingStars(d.rating.stars || 0);
          setRatingComment(d.rating.comment || '');
          setRatingTags(d.rating.tags || []);
        }
      })
      .catch(() => {});
  }, [currentSessionId]);

  // Load saved program names so cards reflect saved state
  useEffect(() => {
    if (!user) { setSavedProgramNames([]); return; }
    fetch(`${API}/saved-recommendations`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { saved: [] })
      .then(d => setSavedProgramNames((d.saved || []).map(s => s.program_name)))
      .catch(() => {});
  }, [user]);

  // Fetch user's chat history and bookmarks when sidebar opens
  useEffect(() => {
    if (showSidebar && user) {
      fetchSidebarData();
    }
  }, [showSidebar, user, sidebarTab]);

  const fetchSidebarData = async () => {
    if (!user) return;
    setLoadingSidebar(true);
    try {
      if (sidebarTab === 'history') {
        const res = await fetch(`${API}/conversations`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setConversations(data.conversations || []);
        }
      } else {
        const res = await fetch(`${API}/saved-recommendations`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setSavedRecs(data.saved || []);
          setSavedProgramNames((data.saved || []).map(s => s.program_name));
        }
      }
    } catch (error) {
      console.error('Failed to fetch sidebar data:', error);
    }
    setLoadingSidebar(false);
  };

  const startNewChat = () => {
    const id = "session_" + Math.random().toString(36).substr(2, 9) + "_" + Date.now();
    localStorage.setItem("cml_session_id", id);
    setCurrentSessionId(id);
    setMessages([]);
    setShowSidebar(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const loadConversation = async (conversationId) => {
    localStorage.setItem("cml_session_id", conversationId);
    setCurrentSessionId(conversationId);
    setShowSidebar(false);
    try {
      const res = await axios.get(API + "/chat/history", { params: { session_id: conversationId } });
      setMessages(res.data.messages || []);
    } catch (e) {
      toast.error("Failed to load conversation");
    }
  };

  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/conversations/${conversationId}`, { method: 'DELETE', credentials: 'include' });
      setConversations(conversations.filter(c => c.conversation_id !== conversationId));
      if (conversationId === currentSessionId) startNewChat();
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const deleteSavedRec = async (savedId) => {
    try {
      await fetch(`${API}/saved-recommendations/${savedId}`, { method: 'DELETE', credentials: 'include' });
      const removed = savedRecs.find(s => s.saved_id === savedId);
      setSavedRecs(savedRecs.filter(s => s.saved_id !== savedId));
      if (removed) setSavedProgramNames(savedProgramNames.filter(n => n !== removed.program_name));
      setSelectedSaved(prev => prev.filter(id => id !== savedId));
      toast.success('Removed from saved');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const toggleSelectSaved = (savedId) => {
    setSelectedSaved(prev => prev.includes(savedId) ? prev.filter(id => id !== savedId) : [...prev, savedId]);
  };

  const toggleSelectAllSaved = () => {
    setSelectedSaved(prev => prev.length === savedRecs.length ? [] : savedRecs.map(s => s.saved_id));
  };

  const toggleCompare = (rec) => {
    setCompareList(prev => {
      const exists = prev.some(p => p.program_name === rec.program_name);
      if (exists) return prev.filter(p => p.program_name !== rec.program_name);
      if (prev.length >= 3) {
        toast.error('You can compare up to 3 programs at a time');
        return prev;
      }
      return [...prev, rec];
    });
  };

  const compareSelectedSaved = () => {
    const chosen = savedRecs.filter(s => selectedSaved.includes(s.saved_id));
    if (chosen.length < 2) {
      toast.error('Tick 2 or 3 saved programs to compare them');
      return;
    }
    if (chosen.length > 3) {
      toast.error('You can compare up to 3 programs at a time');
      return;
    }
    setCompareList(chosen);
    setShowCompare(true);
  };

  const exportSavedCSV = async () => {
    if (savedRecs.length === 0) return;
    setExporting(true);
    try {
      const res = await fetch(`${API}/saved-recommendations/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ saved_ids: selectedSaved }),
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'saved-recommendations.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Filter autocomplete suggestions based on input
  useEffect(() => {
    if (inputMessage.length >= 2) {
      const query = inputMessage.toLowerCase();
      const filtered = AUTOCOMPLETE_SUGGESTIONS.filter(suggestion => 
        suggestion.toLowerCase().includes(query) || 
        suggestion.toLowerCase().startsWith(query)
      ).slice(0, 5);
      
      setAutocompleteOptions(filtered);
      setShowAutocomplete(filtered.length > 0);
      setSelectedAutocompleteIndex(0);
    } else {
      setShowAutocomplete(false);
      setAutocompleteOptions([]);
    }
  }, [inputMessage]);

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAutocompleteSelect = (suggestion) => {
    setInputMessage(suggestion);
    setShowAutocomplete(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showAutocomplete) return;
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedAutocompleteIndex(prev => 
        prev < autocompleteOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedAutocompleteIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === "Tab" || e.key === "Enter") {
      if (autocompleteOptions.length > 0 && showAutocomplete) {
        e.preventDefault();
        handleAutocompleteSelect(autocompleteOptions[selectedAutocompleteIndex]);
      }
    } else if (e.key === "Escape") {
      setShowAutocomplete(false);
    }
  };

  const sendMessage = async (msg) => {
    if (!msg || !msg.trim()) return;

    setShowAutocomplete(false);
    const userMsg = { role: "user", content: msg, timestamp: new Date().toISOString() };
    setMessages(function(prev) { return [...prev, userMsg]; });
    setInputMessage("");
    setIsLoading(true);

    let assistantAdded = false;
    let acc = "";
    const updateLastAssistant = (patch) => {
      setMessages(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant") copy[copy.length - 1] = { ...last, ...patch };
        return copy;
      });
    };

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: currentSessionId, message: msg })
      });
      if (!res.ok || !res.body) throw new Error("stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { value, done: rdone } = await reader.read();
        if (rdone) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const trimmed = part.trim();
          if (!trimmed.startsWith("data:")) continue;
          const payload = trimmed.slice(5).trim();
          if (!payload) continue;
          let data;
          try { data = JSON.parse(payload); } catch (err) { continue; }

          if (data.type === "delta") {
            acc += data.content;
            if (!assistantAdded) {
              assistantAdded = true;
              setIsLoading(false);
              setMessages(prev => [...prev, {
                role: "assistant", content: acc, recommendations: [], assumptions: [],
                query: msg, streaming: true, timestamp: new Date().toISOString()
              }]);
            } else {
              updateLastAssistant({ content: acc, streaming: true });
            }
          } else if (data.type === "done") {
            streamDone = true;
            if (!assistantAdded) {
              assistantAdded = true;
              setIsLoading(false);
              setMessages(prev => [...prev, {
                role: "assistant", content: data.response || acc,
                recommendations: data.recommendations || [], assumptions: data.assumptions || [],
                query: msg, streaming: false, timestamp: new Date().toISOString()
              }]);
            } else {
              updateLastAssistant({
                content: data.response || acc,
                recommendations: data.recommendations || [],
                assumptions: data.assumptions || [],
                streaming: false
              });
            }
          } else if (data.type === "error") {
            throw new Error(data.message || "error");
          }
        }
      }

      if (user) {
        fetch(`${API}/conversations`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : { conversations: [] })
          .then(d => setConversations(d.conversations || []))
          .catch(() => {});
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to get response");
      if (assistantAdded) {
        updateLastAssistant({ streaming: false });
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I encountered an error. Please try again.", timestamp: new Date().toISOString() }]);
      }
    }

    setIsLoading(false);
    if (inputRef.current) inputRef.current.focus();
  };

  // Honor a logged-in user's previously-accepted privacy notice (cross-device)
  useEffect(() => {
    if (user && user.privacy_accepted) {
      setPrivacyAccepted(true);
      localStorage.setItem('cml_privacy_accepted', 'true');
    }
  }, [user]);

  const acceptPrivacy = () => {
    localStorage.setItem('cml_privacy_accepted', 'true');
    setPrivacyAccepted(true);
    if (user) {
      fetch(`${API}/privacy/accept`, { method: 'POST', credentials: 'include' }).catch(() => {});
    }
  };

  const handleAssumptionClick = (editPrompt) => {
    if (!editPrompt) return;
    setInputMessage(editPrompt);
    if (inputRef.current) inputRef.current.focus();
  };

  const toggleRatingTag = (tag) => {
    setRatingTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const submitRating = async () => {
    if (!ratingStars) { toast.error("Please pick a star rating first"); return; }
    setSubmittingRating(true);
    try {
      const res = await fetch(`${API}/ratings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ conversation_id: currentSessionId, stars: ratingStars, comment: ratingComment, tags: ratingTags })
      });
      if (res.ok) {
        toast.success("Thanks for the feedback!");
        setExistingRating({ stars: ratingStars, comment: ratingComment, tags: ratingTags });
        setRatingClosed(true);
      } else {
        toast.error("Failed to submit rating");
      }
    } catch (error) {
      toast.error("Failed to submit rating");
    }
    setSubmittingRating(false);
  };

  const handleLogout = async () => {    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      localStorage.removeItem('user');
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSaveProgram = async (rec, contextQuery) => {
    if (!user) {
      toast.error("Please login to save recommendations");
      navigate('/login');
      return;
    }
    try {
      const res = await fetch(`${API}/saved-recommendations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          program_name: rec.program_name,
          organization: rec.organization || "",
          offer: rec.offer || "",
          cost: rec.cost || "",
          address: rec.address || "",
          website: rec.website || "",
          why_recommended: rec.why_recommended || "",
          more_info: rec.more_info || "",
          context_query: contextQuery || "",
          conversation_id: currentSessionId
        })
      });
      if (res.ok) {
        setSavedProgramNames(prev => prev.includes(rec.program_name) ? prev : [...prev, rec.program_name]);
        toast.success(`Saved "${rec.program_name}"`);
      } else {
        toast.error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save recommendation");
    }
  };

  const handleCopy = async (content, msgIndex) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(msgIndex);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (showAutocomplete && autocompleteOptions.length > 0) {
      // If autocomplete is showing, first select the suggestion
      return;
    }
    sendMessage(inputMessage);
  };

  const showQuickActions = messages.length > 0 && messages[messages.length - 1].role === "assistant" && !isLoading;

  return (
    <div className="min-h-screen bg-[#f5f0e6] flex flex-col">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-9 h-9 bg-[#c9a962] rounded-md flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#1e3a5f]" />
            </div>
            <span className="font-bold text-lg text-[#f5f0e6] hidden sm:inline">ConnectMakeLaunch AI</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/about')}
              className="hidden md:flex items-center px-3 py-1.5 text-sm text-[#f5f0e6]/80 hover:text-[#f5f0e6] transition-colors"
              data-testid="dashboard-about-btn"
            >
              About
            </button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={startNewChat} 
              className="bg-transparent border-[#c9a962]/30 text-[#f5f0e6] hover:bg-[#c9a962]/20"
              data-testid="new-chat-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              New Chat
            </Button>
            
            {user ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setSidebarTab('history'); setShowSidebar(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#c9a962] text-[#1e3a5f] text-sm font-semibold hover:bg-[#d8bd7e] transition-colors shadow-sm"
                  data-testid="open-history-btn"
                  title="Your chat history"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">History</span>
                </button>
                <button
                  onClick={() => { setSidebarTab('saved'); setShowSidebar(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#c9a962] text-[#1e3a5f] text-sm font-semibold hover:bg-[#d8bd7e] transition-colors shadow-sm"
                  data-testid="open-saved-btn"
                  title="Your saved recommendations"
                >
                  <BookMarked className="w-4 h-4" />
                  <span className="hidden sm:inline">Saved</span>
                </button>
                <button
                  onClick={() => setShowConcierge(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1e3a5f] border border-[#c9a962] text-[#c9a962] text-sm font-semibold hover:bg-[#2a4d6e] transition-colors shadow-sm"
                  data-testid="open-concierge-btn"
                  title="Request personalized concierge help"
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="hidden sm:inline">Concierge</span>
                </button>
                <button
                  onClick={() => navigate('/profile-setup')}
                  className="hidden sm:flex items-center gap-2 px-3 py-1 bg-[#c9a962]/20 rounded-full hover:bg-[#c9a962]/30 transition-colors"
                  data-testid="profile-btn"
                  title="View or edit your profile"
                >
                  <div className="w-6 h-6 bg-[#c9a962] rounded-full flex items-center justify-center text-xs font-bold text-[#1e3a5f]">
                    {user.name?.charAt(0) || user.email?.charAt(0) || 'U'}
                  </div>
                  <span className="text-sm text-[#f5f0e6]">{user.name || user.email?.split('@')[0]}</span>
                </button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleLogout}
                  className="text-[#f5f0e6] hover:bg-[#c9a962]/20"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setShowContact(true)}
                  className="hidden sm:flex items-center gap-1.5 px-3 text-[#f5f0e6]/80 text-sm hover:text-[#f5f0e6] transition-colors"
                  data-testid="open-contact-btn-guest"
                  title="Contact us"
                >
                  Contact
                </button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/login')}
                  className="bg-transparent border-[#c9a962]/30 text-[#f5f0e6] hover:bg-[#c9a962]/20"
                  data-testid="header-login-btn"
                >
                  Login to save chats
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar for logged-in users */}
      {user && showSidebar && (
        <div className="fixed inset-y-0 right-0 w-80 bg-white shadow-2xl z-50 flex flex-col animate-slide-in-right">
          {/* Sidebar Header */}
          <div className="bg-[#1e3a5f] p-4 flex items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => setSidebarTab('history')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  sidebarTab === 'history' 
                    ? 'bg-[#c9a962] text-[#1e3a5f]' 
                    : 'text-[#f5f0e6] hover:bg-[#2a4d6e]'
                }`}
              >
                <History className="w-4 h-4 inline mr-1" />
                History
              </button>
              <button
                onClick={() => setSidebarTab('saved')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  sidebarTab === 'saved' 
                    ? 'bg-[#c9a962] text-[#1e3a5f]' 
                    : 'text-[#f5f0e6] hover:bg-[#2a4d6e]'
                }`}
                data-testid="sidebar-saved-tab"
              >
                <BookMarked className="w-4 h-4 inline mr-1" />
                Saved
              </button>
            </div>
            <button
              onClick={() => setShowSidebar(false)}
              className="text-[#f5f0e6] hover:bg-[#2a4d6e] p-1.5 rounded-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Sidebar Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loadingSidebar ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#1e3a5f]" />
              </div>
            ) : sidebarTab === 'history' ? (
              <div className="space-y-3" data-testid="conversations-list">
                <button
                  onClick={startNewChat}
                  className="w-full flex items-center justify-center gap-2 p-2.5 mb-1 rounded-lg bg-[#1e3a5f] text-[#f5f0e6] hover:bg-[#2a4d6e] text-sm font-medium transition-colors"
                  data-testid="sidebar-new-chat-btn"
                >
                  <MessageSquare className="w-4 h-4" /> New Chat
                </button>
                {conversations.length === 0 ? (
                  <div className="text-center py-8 text-[#1e3a5f]/50">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No chat history yet</p>
                    <p className="text-sm mt-1">Your conversations will appear here</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.conversation_id}
                      onClick={() => loadConversation(conv.conversation_id)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors group ${
                        conv.conversation_id === currentSessionId ? 'bg-[#e8e0d0] ring-1 ring-[#c9a962]' : 'bg-[#f5f0e8] hover:bg-[#e8e0d0]'
                      }`}
                      data-testid={`conversation-${conv.conversation_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1e3a5f] line-clamp-2">
                            {conv.title || 'New conversation'}
                          </p>
                          <p className="text-xs text-[#1e3a5f]/50 mt-1">
                            {conv.updated_at ? new Date(conv.updated_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                        <button
                          onClick={(e) => deleteConversation(conv.conversation_id, e)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-100 rounded text-red-500 transition-opacity"
                          title="Delete conversation"
                          data-testid={`delete-conversation-${conv.conversation_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="space-y-3" data-testid="saved-recommendations-list">
                {savedRecs.length === 0 ? (
                  <div className="text-center py-8 text-[#1e3a5f]/50">
                    <BookMarked className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No saved recommendations</p>
                    <p className="text-sm mt-1">Click "Save" on any recommended program to keep it here</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2 pb-2 border-b border-[#1e3a5f]/10" data-testid="saved-export-toolbar">
                      <label className="flex items-center gap-2 text-xs text-[#1e3a5f] cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={savedRecs.length > 0 && selectedSaved.length === savedRecs.length}
                          onChange={toggleSelectAllSaved}
                          className="accent-[#1e3a5f] w-4 h-4"
                          data-testid="select-all-saved"
                        />
                        Select all
                      </label>
                      <button
                        onClick={compareSelectedSaved}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#1e3a5f]/30 text-[#1e3a5f] text-xs font-medium hover:bg-[#e8e0d0] transition-colors"
                        data-testid="compare-saved-btn"
                        title="Select 2-3 programs to compare"
                      >
                        <Columns className="w-4 h-4" />
                        Compare
                      </button>
                      <button
                        onClick={exportSavedCSV}
                        disabled={exporting}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-[#f5f0e6] text-xs font-medium hover:bg-[#2a4d6e] disabled:opacity-50 transition-colors"
                        data-testid="download-csv-btn"
                      >
                        {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {selectedSaved.length > 0 ? `Download ${selectedSaved.length}` : 'Download all'}
                      </button>
                    </div>
                    {savedRecs.map((rec) => (
                    <div
                      key={rec.saved_id}
                      className="p-3 bg-[#f5f0e8] rounded-lg group"
                      data-testid={`saved-rec-${rec.saved_id}`}
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={selectedSaved.includes(rec.saved_id)}
                          onChange={() => toggleSelectSaved(rec.saved_id)}
                          className="accent-[#1e3a5f] w-4 h-4 mt-0.5 shrink-0"
                          data-testid={`select-saved-${rec.saved_id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-[#1e3a5f]">{rec.program_name}</p>
                          {rec.organization && <p className="text-xs text-[#1e3a5f]/70">{rec.organization}</p>}
                          {rec.cost && <p className="text-xs text-[#1e3a5f] mt-1">{rec.cost}</p>}
                          {rec.website && rec.website.startsWith('http') && (
                            <a href={rec.website} target="_blank" rel="noopener noreferrer" className="text-xs text-[#c9a962] underline break-all">{rec.website}</a>
                          )}
                          {rec.context_query && (
                            <p className="text-xs text-[#1e3a5f]/50 mt-2 italic">Recommended when you asked: "{rec.context_query.substring(0, 60)}{rec.context_query.length > 60 ? '…' : ''}"</p>
                          )}
                        </div>
                        <button
                          onClick={() => deleteSavedRec(rec.saved_id)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-100 rounded text-red-500 transition-opacity"
                          title="Remove"
                          data-testid={`delete-saved-${rec.saved_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sidebar Overlay */}
      {user && showSidebar && (
        <div 
          className="fixed inset-0 bg-black/20 z-40"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden">
          <ScrollArea className="flex-1">
            <div className="max-w-4xl mx-auto">
              {/* Welcome State */}
              {messages.length === 0 && (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <MessageSquare className="w-8 h-8 text-[#c9a962]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#1e3a5f] mb-3">
                    How can I help you today?
                  </h2>
                  <p className="text-[#1e3a5f]/60 mb-8 max-w-md mx-auto">
                    Tell me about your startup and what you're looking for. I'll help you find the right resources.
                  </p>
                  
                  <div className="flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
                    {suggestions.map((s, i) => (
                      <button 
                        key={s} 
                        className="suggestion-chip text-left" 
                        onClick={() => sendMessage(s)} 
                        data-testid={"suggestion-" + i}
                      >
                        <Sparkles className="w-3 h-3 mr-2 text-[#c9a962] shrink-0" />
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Messages */}
              {messages.map((message, index) => (
                <ChatMessage 
                  key={`msg-${index}-${message.timestamp || ''}`} 
                  message={message} 
                  index={index}
                  onCopy={() => handleCopy(message.content, index)}
                  onSaveProgram={(rec) => handleSaveProgram(rec, message.query || messages[index - 1]?.content || '')}
                  onAssumption={handleAssumptionClick}
                  savedProgramNames={savedProgramNames}
                  compareNames={compareList.map(c => c.program_name)}
                  onToggleCompare={toggleCompare}
                  isCopied={copiedId === index}
                  isUser={message.role === "user"}
                  showActions={message.role === "assistant"}
                />
              ))}
              
              {isLoading && <TypingIndicator />}

              {/* Whole-experience rating card */}
              {(messages.filter(m => m.role === "assistant").length >= 1 && !isLoading && !ratingClosed && !messages[messages.length - 1]?.streaming) && (
                <div className="px-4 pt-4 max-w-4xl mx-auto w-full" data-testid="rating-card">
                  <div className="bg-white border border-[#e8e0d0] rounded-xl p-5 relative">
                    <button
                      onClick={() => setRatingClosed(true)}
                      className="absolute top-3 right-3 text-[#1e3a5f]/40 hover:text-[#1e3a5f] p-1 rounded-md hover:bg-[#f5f0e6]"
                      title="Dismiss"
                      data-testid="rating-dismiss-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="font-bold text-[#1e3a5f]">{existingRating ? "Update your rating" : "How was this experience?"}</p>
                    <p className="text-sm text-[#1e3a5f]/60 mt-0.5">Your feedback helps us improve the recommendations.</p>
                    <div className="flex items-center gap-1.5 mt-3">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          onClick={() => setRatingStars(n)}
                          className="p-0.5 transition-transform hover:scale-110"
                          title={`${n} star${n > 1 ? 's' : ''}`}
                          data-testid={`rating-star-${n}`}
                        >
                          <Star className={`w-7 h-7 ${n <= ratingStars ? 'fill-[#c9a962] text-[#c9a962]' : 'text-[#d8cfbc]'}`} />
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="rating-tags">
                      {RATING_TAGS.map((tag) => {
                        const active = ratingTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleRatingTag(tag)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                              active
                                ? 'bg-[#1e3a5f] text-[#f5f0e6] border-[#1e3a5f]'
                                : 'bg-white text-[#1e3a5f] border-[#e8e0d0] hover:border-[#1e3a5f]'
                            }`}
                            data-testid={`rating-tag-${tag.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}
                          >
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                    <textarea
                      value={ratingComment}
                      onChange={(e) => setRatingComment(e.target.value)}
                      placeholder="Optional: tell us what worked or what didn't…"
                      rows={2}
                      className="w-full mt-3 text-sm border border-[#e8e0d0] rounded-lg p-2.5 focus:outline-none focus:border-[#1e3a5f] resize-none"
                      data-testid="rating-comment-input"
                    />
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={submitRating}
                        disabled={submittingRating || !ratingStars}
                        className="flex items-center gap-1.5 bg-[#1e3a5f] text-[#f5f0e6] text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#2a4d6e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        data-testid="rating-submit-btn"
                      >
                        {submittingRating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Submit feedback
                      </button>
                      <button
                        onClick={() => setRatingClosed(true)}
                        className="text-sm text-[#1e3a5f]/60 hover:text-[#1e3a5f] px-3 py-2"
                        data-testid="rating-skip-btn"
                      >
                        Not now
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Quick Action Buttons */}
              {showQuickActions && (
                <div className="px-4 py-4 max-w-4xl mx-auto">
                  <div className="flex flex-wrap gap-2 justify-center">
                    {QUICK_ACTIONS.map((action, i) => (
                      <Button
                        key={action.label}
                        variant="outline"
                        size="sm"
                        onClick={() => sendMessage(action.message)}
                        className="bg-white border-[#e8e0d0] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-[#f5f0e6] hover:border-[#1e3a5f] transition-all"
                        data-testid={"quick-action-" + i}
                      >
                        <action.icon className="w-4 h-4 mr-2" />
                        {action.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input Area with Autocomplete */}
          <div className="border-t border-[#e8e0d0] bg-white p-4">
            <form onSubmit={handleSubmit} className="max-w-4xl mx-auto relative" ref={autocompleteRef}>
              {/* Autocomplete Dropdown */}
              {showAutocomplete && autocompleteOptions.length > 0 && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-[#e8e0d0] rounded-lg shadow-lg overflow-hidden z-50">
                  {autocompleteOptions.map((option, index) => (
                    <button
                      key={option}
                      type="button"
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-2 transition-colors ${
                        index === selectedAutocompleteIndex 
                          ? "bg-[#1e3a5f] text-[#f5f0e6]" 
                          : "text-[#1e3a5f] hover:bg-[#f5f0e6]"
                      }`}
                      onClick={() => handleAutocompleteSelect(option)}
                      data-testid={"autocomplete-" + index}
                    >
                      <Sparkles className={`w-3 h-3 ${index === selectedAutocompleteIndex ? "text-[#c9a962]" : "text-[#c9a962]"}`} />
                      <span>{option}</span>
                      {index === selectedAutocompleteIndex && (
                        <span className="ml-auto text-xs opacity-70">Tab to select</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              
              <div className="flex gap-3">
                <Input 
                  ref={inputRef} 
                  placeholder="Start typing to see suggestions..." 
                  value={inputMessage} 
                  onChange={(e) => { setInputMessage(e.target.value); if (e.target.value.trim()) setHasTyped(true); }}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading} 
                  className="flex-1 h-12 chat-input border-[#e8e0d0] focus:border-[#1e3a5f]" 
                  data-testid="chat-input"
                  autoComplete="off"
                />
                <Button 
                  type="submit" 
                  disabled={isLoading || !inputMessage.trim()} 
                  className="bg-[#1e3a5f] hover:bg-[#2a4d6e] text-[#f5f0e6] h-12 px-6" 
                  data-testid="send-message-btn"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ContactModal open={showContact} onClose={() => setShowContact(false)} mode="contact" />
      <ContactModal open={showConcierge} onClose={() => setShowConcierge(false)} mode="concierge" user={user} />

      {privacyAccepted && (
        <ExperienceFeedbackWidget
          userMessageCount={userMessageCount}
          visitId={visitId}
          sessionId={currentSessionId}
        />
      )}

      {/* Compare tray */}
      {compareList.length > 0 && !showCompare && (
        <div className="fixed bottom-5 left-5 z-[140] bg-white border border-[#e8e0d0] rounded-2xl shadow-xl p-3 max-w-[22rem]" data-testid="compare-tray">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-sm font-semibold text-[#1e3a5f]">Comparing {compareList.length} of 3</p>
            <button onClick={() => setCompareList([])} className="text-[#1e3a5f]/40 hover:text-[#1e3a5f] p-1 rounded-md hover:bg-[#f5f0e6]" data-testid="compare-tray-clear-btn" aria-label="Clear comparison">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {compareList.map((c) => (
              <span key={c.program_name} className="text-xs bg-[#f5f0e6] border border-[#e8e0d0] text-[#1e3a5f] px-2 py-0.5 rounded-full">{c.program_name}</span>
            ))}
          </div>
          <button
            onClick={() => compareList.length >= 2 ? setShowCompare(true) : toast.error('Pick one more program to compare')}
            className="w-full bg-[#1e3a5f] text-[#f5f0e6] py-2 rounded-xl text-sm font-semibold hover:bg-[#2a4d6e] transition-colors"
            data-testid="compare-tray-open-btn"
          >
            Compare side by side
          </button>
        </div>
      )}

      {showCompare && (
        <ProgramCompareModal programs={compareList} onClose={() => setShowCompare(false)} />
      )}

      {/* Privacy notice gate — blocks the chat until accepted */}
      {!privacyAccepted && (
        <div className="fixed inset-0 bg-[#1e3a5f]/70 backdrop-blur-sm z-[200] flex items-center justify-center p-4" data-testid="privacy-gate">
          <div className="bg-[#f5f0e8] rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
            <div className="p-5 border-b border-[#e8e0d0]">
              <h2 className="text-lg font-bold text-[#1e3a5f]">Privacy Notice</h2>
            </div>
            <div className="p-5 overflow-y-auto text-sm text-[#1e3a5f]/90 space-y-3">
              <p>ConnectMakeLaunch AI is a chatbot tool provided by RIDC to help connect you with regional business and workforce resources.</p>
              <p className="font-medium text-[#1e3a5f]">Please avoid entering personal identifying information (e.g., Social Security numbers, financial account details, or other sensitive personal data) in your chat.</p>
              <p>By using this tool, you acknowledge and agree that:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>RIDC may use your inputs — including chat queries and feedback — for research, quality control, service improvement, and analysis, for any period of time.</li>
                <li>This tool relies on third-party AI and infrastructure providers. While we take reasonable care in selecting these providers, RIDC cannot guarantee or control their data handling practices.</li>
                <li>RIDC may share summarized or aggregated data with partner organizations to support regional program planning.</li>
                <li>This is not a secure channel for confidential or sensitive business information.</li>
              </ul>
            </div>
            <div className="p-5 border-t border-[#e8e0d0]">
              <label className="flex items-start gap-2 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={privacyChecked}
                  onChange={(e) => setPrivacyChecked(e.target.checked)}
                  className="accent-[#1e3a5f] w-4 h-4 mt-0.5 shrink-0"
                  data-testid="privacy-checkbox"
                />
                <span className="text-sm text-[#1e3a5f]">I have read and agree to the above.</span>
              </label>
              <button
                onClick={acceptPrivacy}
                disabled={!privacyChecked}
                className="w-full bg-[#1e3a5f] text-[#f5f0e6] py-3 rounded-xl font-semibold hover:bg-[#2a4d6e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="privacy-continue-btn"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const RATING_TAGS = [
  "I like the tool",
  "Helpful recommendations",
  "I might contact one of these",
  "I might come back again",
  "Easy to use",
  "Didn't find what I needed",
  "Not useful",
];

const REMARK_PLUGINS = [remarkGfm];

const MARKDOWN_COMPONENTS = {
  h1: ({children}) => <h1 className="text-xl font-bold mt-4 mb-2 text-[#1e3a5f]">{children}</h1>,
  h2: ({children}) => <h2 className="text-lg font-bold mt-4 mb-2 text-[#1e3a5f]">{children}</h2>,
  h3: ({children}) => <h3 className="text-base font-bold mt-3 mb-1 text-[#1e3a5f]">{children}</h3>,
  p: ({children}) => <p className="mb-2">{children}</p>,
  ul: ({children}) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
  ol: ({children}) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
  li: ({children}) => <li>{children}</li>,
  strong: ({children}) => <strong className="font-semibold text-[#1e3a5f]">{children}</strong>,
  code: ({children}) => <code className="bg-[#f5f0e6] px-1 py-0.5 rounded text-sm">{children}</code>,
  blockquote: ({children}) => <blockquote className="border-l-4 border-[#c9a962] pl-4 italic my-2">{children}</blockquote>,
  hr: () => <hr className="my-4 border-[#e8e0d0]" />,
  a: ({href, children}) => <a href={href} className="text-[#1e3a5f] underline hover:text-[#2a4d6e]" target="_blank" rel="noopener noreferrer">{children}</a>,
  table: ({children}) => <div className="overflow-x-auto my-4 rounded-lg border border-[#e8e0d0]"><table className="w-full border-collapse text-sm">{children}</table></div>,
  thead: ({children}) => <thead className="bg-[#1e3a5f]">{children}</thead>,
  tbody: ({children}) => <tbody className="bg-white">{children}</tbody>,
  tr: ({children}) => <tr className="border-b border-[#e8e0d0] last:border-b-0">{children}</tr>,
  th: ({children}) => <th className="px-4 py-3 text-left font-semibold text-[#f5f0e6] whitespace-nowrap">{children}</th>,
  td: ({children}) => <td className="px-4 py-3 text-[#1e3a5f] border-r border-[#e8e0d0] last:border-r-0">{children}</td>,
};

function ProgramCard({ rec, onSave, isSaved, index, inCompare = false, onToggleCompare }) {
  const [showInfo, setShowInfo] = useState(false);
  const hasUrl = rec.website && rec.website.startsWith('http');
  return (
    <div className="border border-[#e8e0d0] rounded-xl bg-white overflow-hidden" data-testid={`program-card-${index}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="font-bold text-[#1e3a5f] leading-snug">{rec.program_name}</h4>
            {rec.organization && <p className="text-sm text-[#1e3a5f]/60">{rec.organization}</p>}
          </div>
          {rec.cost && <span className="shrink-0 text-sm font-medium text-[#1e3a5f] bg-[#f5f0e6] px-2.5 py-1 rounded-full whitespace-nowrap">{rec.cost}</span>}
        </div>
        {rec.offer && <p className="text-sm text-[#1e3a5f] mt-2">{rec.offer}</p>}
        {rec.fit === 'partial' && rec.fit_note && (
          <div className="flex items-start gap-2 mt-2 text-xs text-[#8a6d1f] bg-[#f5efd8] border border-[#e6d9a8] rounded-lg px-2.5 py-1.5" data-testid={`fit-note-${index}`}>
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{rec.fit_note}</span>
          </div>
        )}
        <div className="flex flex-col gap-1 mt-3 text-sm">
          {rec.address && (
            <div className="flex items-start gap-2 text-[#1e3a5f]/70">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#c9a962]" />
              <span>{rec.address}</span>
            </div>
          )}
          {rec.website && (
            <div className="flex items-start gap-2 text-[#1e3a5f]/70">
              <ExternalLink className="w-4 h-4 mt-0.5 shrink-0 text-[#c9a962]" />
              {hasUrl ? (
                <a href={rec.website} target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline hover:text-[#2a4d6e] break-all">{rec.website}</a>
              ) : (
                <span>{rec.website}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={() => onSave(rec)}
            disabled={isSaved}
            className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
              isSaved ? 'bg-green-100 text-green-700 cursor-default' : 'bg-[#1e3a5f] text-[#f5f0e6] hover:bg-[#2a4d6e]'
            }`}
            data-testid={`save-program-btn-${index}`}
          >
            {isSaved ? <><BookmarkCheck className="w-4 h-4" /> Saved</> : <><Bookmark className="w-4 h-4" /> Save</>}
          </button>
          {(rec.more_info || rec.why_recommended) && (
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-[#e8e0d0] text-[#1e3a5f] hover:bg-[#f5f0e6] transition-colors"
              data-testid={`info-program-btn-${index}`}
            >
              <Info className="w-4 h-4" /> Info
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showInfo ? 'rotate-180' : ''}`} />
            </button>
          )}
          {onToggleCompare && (
            <button
              onClick={() => onToggleCompare(rec)}
              className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                inCompare ? 'bg-[#c9a962] border-[#c9a962] text-[#1e3a5f]' : 'border-[#e8e0d0] text-[#1e3a5f] hover:bg-[#f5f0e6]'
              }`}
              data-testid={`compare-program-btn-${index}`}
              title="Add to side-by-side comparison"
            >
              <Columns className="w-4 h-4" /> {inCompare ? 'Comparing' : 'Compare'}
            </button>
          )}
        </div>
      </div>
      {showInfo && (
        <div className="border-t border-[#e8e0d0] bg-[#f5f0e6]/50 p-4 space-y-2" data-testid={`program-info-${index}`}>
          {rec.why_recommended && (
            <div>
              <p className="text-xs font-semibold text-[#c9a962] uppercase tracking-wide">Why recommended</p>
              <p className="text-sm text-[#1e3a5f]">{rec.why_recommended}</p>
            </div>
          )}
          {rec.more_info && (
            <div>
              <p className="text-xs font-semibold text-[#c9a962] uppercase tracking-wide">More about this program</p>
              <p className="text-sm text-[#1e3a5f]">{rec.more_info}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChatMessage({ message, index, onCopy, onSaveProgram, onAssumption, savedProgramNames = [], compareNames = [], onToggleCompare, isCopied, isUser, showActions }) {
  const recommendations = message.recommendations || [];
  const assumptions = message.assumptions || [];
  return (
    <div className={"message-block " + message.role + " animate-fade-in-up"} data-testid={"message-" + index}>
      <div className="max-w-4xl mx-auto flex gap-4">
        <div className={"w-8 h-8 rounded-full flex items-center justify-center shrink-0 " + (isUser ? "bg-[#e8e0d0]" : "bg-[#1e3a5f]")}>
          {isUser ? <User className="w-4 h-4 text-[#1e3a5f]" /> : <Sparkles className="w-4 h-4 text-[#c9a962]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium text-[#1e3a5f]/50">{isUser ? "You" : "ConnectMakeLaunch AI"}</p>
            {showActions && (
              <div className="flex items-center gap-1">
                <button
                  onClick={onCopy}
                  className="p-1.5 rounded-md hover:bg-[#f5f0e6] text-[#1e3a5f]/50 hover:text-[#1e3a5f] transition-colors"
                  title="Copy to clipboard"
                  data-testid={`copy-btn-${index}`}
                >
                  {isCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            )}
          </div>
          {message.content && message.content.trim() && (
            <div className="text-[#1e3a5f] prose prose-sm max-w-none">
              {isUser ? (
                <p className="whitespace-pre-wrap">{message.content}</p>
              ) : (
                <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
                  {message.content}
                </ReactMarkdown>
              )}
            </div>
          )}
          {!isUser && message.streaming && recommendations.length === 0 && (
            <div className="flex items-center gap-2 mt-3 text-sm text-[#1e3a5f]/60" data-testid={`finding-programs-${index}`}>
              <Loader2 className="w-4 h-4 animate-spin text-[#c9a962]" />
              <span className="animate-pulse">Finding matching programs…</span>
            </div>
          )}
          {!isUser && assumptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid={`assumptions-${index}`}>
              <span className="text-xs text-[#1e3a5f]/50">I assumed — tap to correct:</span>
              {assumptions.map((a, i) => (
                <button
                  key={i}
                  onClick={() => onAssumption && onAssumption(a.edit_prompt)}
                  className="flex items-center gap-1 text-xs bg-[#f5efd8] border border-[#e6d9a8] text-[#8a6d1f] px-2.5 py-1 rounded-full hover:bg-[#efe6c4] transition-colors"
                  title="Tap to correct this assumption"
                  data-testid={`assumption-chip-${index}-${i}`}
                >
                  {a.label}
                  <Pencil className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}
          {recommendations.length > 0 && (() => {
            const exact = recommendations.filter(r => (r.fit || 'exact') !== 'partial');
            const partial = recommendations.filter(r => r.fit === 'partial');
            return (
              <div className="mt-3 space-y-3" data-testid={`recommendations-${index}`}>
                {exact.length > 0 && (
                  <div className="grid grid-cols-1 gap-3">
                    {exact.map((rec, i) => (
                      <ProgramCard
                        key={`${rec.program_name}-${i}`}
                        rec={rec}
                        index={i}
                        isSaved={savedProgramNames.includes(rec.program_name)}
                        onSave={onSaveProgram}
                        inCompare={compareNames.includes(rec.program_name)}
                        onToggleCompare={onToggleCompare}
                      />
                    ))}
                  </div>
                )}
                {partial.length > 0 && (
                  <div className="space-y-3" data-testid={`partial-recommendations-${index}`}>
                    <div className="flex items-start gap-2 text-sm text-[#1e3a5f]/70 border-t border-[#e8e0d0] pt-3">
                      <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#c9a962]" />
                      <span>These may not meet all your needs (for example, they're not based in your exact area), but could still be relevant:</span>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      {partial.map((rec, i) => (
                        <ProgramCard
                          key={`${rec.program_name}-p${i}`}
                          rec={rec}
                          index={exact.length + i}
                          isSaved={savedProgramNames.includes(rec.program_name)}
                          onSave={onSaveProgram}
                          inCompare={compareNames.includes(rec.program_name)}
                          onToggleCompare={onToggleCompare}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

const THINKING_PHRASES = [
  "Scanning nearly 500 Pittsburgh programs…",
  "Matching resources to your stage and industry…",
  "Checking eligibility and location fit…",
  "Weighing funding, grants and accelerators…",
  "Comparing cost and value for you…",
  "Filtering for the best-fit programs…",
  "Reading through program details…",
  "Lining up your top recommendations…",
  "Double-checking geographic coverage…",
  "Sorting exact matches from close ones…",
];

function TypingIndicator() {
  const [phraseIndex, setPhraseIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % THINKING_PHRASES.length);
    }, 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="message-block assistant animate-fade-in-up">
      <div className="max-w-4xl mx-auto flex gap-4">
        <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-[#c9a962]" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-[#1e3a5f]/50 mb-1">ConnectMakeLaunch AI</p>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-[#1e3a5f]/40 rounded-full typing-dot" />
              <div className="w-2 h-2 bg-[#1e3a5f]/40 rounded-full typing-dot" />
              <div className="w-2 h-2 bg-[#1e3a5f]/40 rounded-full typing-dot" />
            </div>
            <span key={phraseIndex} className="text-sm text-[#1e3a5f]/60 italic animate-fade-in-up" data-testid="thinking-phrase">
              {THINKING_PHRASES[phraseIndex]}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
